import { useMemo } from 'react'
import moment from 'moment'
import { faSquareDollar } from '@fortawesome/pro-regular-svg-icons'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useReselector } from '@/utils/sharedHooks'
import { useNavigate } from 'react-router-dom'

import { Text, Button, Card, Image, CardColors } from '@/ui'
import { TypographyStyles, TextColors } from '@/styles'
import { FEATURE_FLAG_KEYS } from '@/flags'

import { DeadlineLabel } from '../DeadlineLabel'
import { LatePaymentRiskCallout } from '../LatePaymentRiskCallout'
import { DATE_FORMATS, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { UserTaxEstimate } from '@/features/taxes/quarterly/userTaxEstimates.slice'
import {
  EstimateStatus,
  statusForSingleEstimate,
} from '@/features/taxes/quarterly/service'
import { SafeHarborPaymentCard } from './SafeHarborPaymentCard'
import { StateSafeHarborCard } from './StateSafeHarborCard'
import { WhatIsSafeHarborPopup } from './helpers'

import {
  selectActiveQTEDetailsAsDates,
  selectActiveQuarterlyTaxEstimateDetails,
  selectIsPastOrSameAsTaxEstimateIRSPaymentDate,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

const FederalSafeHarborCard = () => {
  const navigate = useNavigate()

  const activeQuarterDetails = useReselector(selectActiveQTEDetailsAsDates)

  if (!activeQuarterDetails) {
    return null
  }

  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 items-start">
        {/* Image Column */}
        <div className="w-full lg:w-[150px] lg:shrink-0 flex justify-center">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/writing_check.svg"
            alt="Writing Check Illustration"
            className="w-[150px] h-[150px]"
          />
        </div>

        {/* Content Column */}
        <div className="w-full lg:flex-1 space-y-4">
          <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
            important note
          </Text>

          <Text variant={TypographyStyles.H2}>
            Paying Q{activeQuarterDetails.taxQuarter} Federal Quarterly Taxes
          </Text>

          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            Since you signed up after{' '}
            {activeQuarterDetails.withOffset.newUserCutOff.toFormat(
              DATE_FORMATS_LUXON.DISPLAY_LONG
            )}
            , we won&apos;t be able to provide a calculated quarterly tax
            estimate using your practice income this quarter. This quarter, you
            can use our safe harbor estimate calculator to determine an estimate
            that will help minimize federal underpayment penalties.
          </Text>

          <div>
            <WhatIsSafeHarborPopup variant="link" />
          </div>

          <Button
            onClick={() => {
              navigate('/taxes/quarterly/calculate-safe-harbor')
            }}
            className="w-full sm:w-auto"
          >
            Calculate safe harbor
          </Button>
        </div>
      </div>
    </Card>
  )
}

export const UserJoinedTooLate = ({
  activeQuarterText,
  federalTaxEstimate,
}: {
  activeQuarterText: string
  federalTaxEstimate: UserTaxEstimate | null
}) => {
  const activeQuarterDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )
  const isPastPaymentDate = useReselector(
    selectIsPastOrSameAsTaxEstimateIRSPaymentDate
  )
  const alwaysOnQteEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.alwaysOnQteEnabled,
    false
  )
  const inLatePaymentWindow = alwaysOnQteEnabled && isPastPaymentDate

  const showsPaymentCard = useMemo(
    () =>
      Boolean(
        federalTaxEstimate &&
        federalTaxEstimate.estimateInCents >= 0 &&
        federalTaxEstimate.safeHarborType
      ),
    [federalTaxEstimate]
  )

  const federalContent = useMemo(() => {
    if (!activeQuarterDetails || !federalTaxEstimate) {
      return null
    }
    return showsPaymentCard ? (
      <SafeHarborPaymentCard federalTaxEstimate={federalTaxEstimate} />
    ) : (
      <FederalSafeHarborCard />
    )
  }, [federalTaxEstimate, activeQuarterDetails, showsPaymentCard])

  // Read from the prop rather than selectStatusForLateJoinerTaxEstimate, which
  // resolves the estimate from the store by id instead of the one passed in.
  const estimateStatus = statusForSingleEstimate(federalTaxEstimate)
  const estimateSettled =
    estimateStatus === EstimateStatus.paid ||
    estimateStatus === EstimateStatus.estimateIsZero

  return (
    <div className="space-y-6">
      {federalContent && !estimateSettled && (
        <LatePaymentRiskCallout>
          {showsPaymentCard
            ? null
            : 'Calculate your safe harbor estimate below to get your payment amount.'}
        </LatePaymentRiskCallout>
      )}

      <Text variant={TypographyStyles.H2} className="flex text-center">
        {activeQuarterText} Estimates
      </Text>

      <div className="p-0">
        <DeadlineLabel
          icon={faSquareDollar}
          text={inLatePaymentWindow ? 'IRS payment was due' : 'IRS payment due'}
          prominentText={moment
            .utc(activeQuarterDetails?.irsPaymentDueAt)
            .format(DATE_FORMATS.DISPLAY_LONG)}
        />
      </div>

      {federalContent}

      <StateSafeHarborCard />
    </div>
  )
}
